import { useState, useRef, useEffect, useCallback, useMemo, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ScheduleItem,
  type ScheduleLink,
  type ScheduleSettings,
  type HolidayInfo,
  type GanttBarChange,
  toISODate,
  parseISODate,
  getBarColor,
  getStatusColor,
  formatDateLabel,
  getDayShort,
  getTaskDisplayTitle,
  sortScheduleItemsByDate,
  compareScheduleItemsByDate,
  shiftToWorkingDay,
  calculateEndDate,
  countWorkingDaysInclusive,
  getContrastColor,
  isScheduleItemIncluded,
} from '@/lib/scheduleUtils';
import type { ScheduleChecklistProgress } from '@/hooks/useScheduleChecklistProgress';

/* ── dev perf logging ───────────────────────────────────────── */
import { JPH_DEBUG } from '@/lib/jphDebug';

function perfLog(label: string, ms: number, extra?: Record<string, number>) {
  if (JPH_DEBUG) {
    // eslint-disable-next-line no-console
    console.log(`[SchedulePerf] ${label}: ${ms.toFixed(1)}ms`, extra || '');
  }
}

/* ── geometry constants ────────────────────────────────────────── */
const ROW_HEIGHT = 48;
const GROUP_ROW_HEIGHT = 32;
const BAR_HEIGHT = 22;
const BAR_LABEL_MIN_WIDTH = 56;
const HEADER_H = 48;
/** Full: all columns. Compact: title, start, duration (+ optional include checkbox). */
const GANTT_LEFT_GRID_FULL = '28px 40px minmax(180px, 1fr) 50px 80px 80px 40px 28px';
const GANTT_LEFT_GRID_COMPACT = '28px minmax(140px, 1fr) 76px 44px 32px';
const GANTT_LEFT_GRID_COMPACT_READONLY = 'minmax(140px, 1fr) 76px 44px 32px';
const GANTT_LEFT_MIN_W_FULL = 546;
const GANTT_LEFT_MIN_W_COMPACT = 420;
const GANTT_LEFT_WIDTH_KEY = 'jph_gantt_left_panel_width';
const GANTT_LEFT_MIN = 280;
const GANTT_LEFT_MAX = 720;
const PAN_THRESHOLD_PX = 4;
const GANTT_PAN_SURFACE_CLASS = 'gantt-pan-surface';
const GANTT_PAN_ACTIVE_CLASS = 'gantt-pan-active';
const NODE_SIZE = 4;
const LINK_LINE_COLOR = '#CBD5E1';
const LINK_LINE_ACTIVE = '#94A3B8';
const VIRTUAL_BUFFER = 4;

function getDayWidth(scale: 'day' | 'week' | 'month' | 'year'): number {
  switch (scale) {
    case 'day': return 52;
    case 'week': return 36;
    case 'month': return 14;
    case 'year': return 6;
    default: return 36;
  }
}

function getDayLabel(scale: 'day' | 'week' | 'month' | 'year', dateStr: string): string {
  const d = parseISODate(dateStr);
  return String(d.getDate());
}

function getShortDayLabel(scale: 'day' | 'week' | 'month' | 'year', dateStr: string): string {
  if (scale === 'year') return '';
  return getDayShort(dateStr);
}

function isPanBlockedTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return !!target.closest(
    '[data-gantt-bar],[data-gantt-interactive],button,a,input,textarea,select,label,[role="button"]'
  );
}

function isNearChartScrollbar(clientX: number, clientY: number, el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();
  const verticalScrollbar = el.offsetWidth - el.clientWidth;
  const horizontalScrollbar = el.offsetHeight - el.clientHeight;
  if (verticalScrollbar > 0 && clientX >= rect.right - verticalScrollbar - 2) return true;
  if (horizontalScrollbar > 0 && clientY >= rect.bottom - horizontalScrollbar - 2) return true;
  return false;
}

interface Props {
  items: ScheduleItem[];
  allItems: ScheduleItem[];
  links?: ScheduleLink[];
  showAllLinks?: boolean;
  settings: ScheduleSettings;
  holidays: HolidayInfo[];
  criticalItemIds: Set<string>;
  computedRisk: Map<string, { delay_status: ScheduleItem['delay_status']; schedule_risk_level: ScheduleItem['schedule_risk_level']; delayDays: number }>;
  canEdit: boolean;
  onEdit: (item: ScheduleItem) => void;
  onToggleComplete: (item: ScheduleItem, checked: boolean) => void;
  onToggleIncluded: (item: ScheduleItem, checked: boolean) => void;
  onIncludeAll: () => void;
  onExcludeAll: () => void;
  showExcluded?: boolean;
  isFullscreen?: boolean;
  scale?: 'day' | 'week' | 'month' | 'year';
  showPhases?: boolean;
  showCriticalPathOnly?: boolean;
  onRecalcCriticalPath?: () => void;
  checklistProgressMap?: Map<string, ScheduleChecklistProgress>;
  onOpenCompletionBlock?: (item: ScheduleItem) => void;
  onBarChange?: (change: GanttBarChange) => void;
  onCreateAtDate?: (dateStr: string) => void;
  scheduleUpdating?: boolean;
  listLayout?: 'full' | 'compact';
  showIncludeColumn?: boolean;
  /** When true, fill parent height instead of viewport calc (embedded subcontractor view). */
  fillHeight?: boolean;
}

export interface GanttRef {
  scrollToToday: () => void;
}

/* ── types for grouped display rows ──────────────────────────── */
type DisplayRow =
  | { type: 'group'; groupKey: string; groupName: string; count: number }
  | { type: 'item'; item: ScheduleItem; originalIndex: number };

/* ── helpers ─────────────────────────────────────────────────── */
function dayOffset(dateStr: string, rangeStart: string): number {
  const s = parseISODate(rangeStart);
  const d = parseISODate(dateStr);
  return Math.round((d.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
}

function dateFromIndex(index: number, rangeStart: string): string {
  const s = parseISODate(rangeStart);
  const d = new Date(s);
  d.setDate(s.getDate() + index);
  return toISODate(d);
}

function buildDayInfos(
  rangeStart: string,
  dayCount: number,
  settings: ScheduleSettings,
  holidayDateSet: Set<string>,
  holidays: HolidayInfo[]
) {
  const start = parseISODate(rangeStart);
  const todayIso = toISODate(new Date());
  const arr: {
    dateStr: string;
    dow: number;
    isNonWorking: boolean;
    isToday: boolean;
    holidayName?: string;
  }[] = [];
  for (let i = 0; i < dayCount; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const dateStr = toISODate(d);
    const isNonWorking = !(
      settings.working_days.includes(d.getDay()) &&
      (!settings.exclude_public_holidays || !holidayDateSet.has(dateStr))
    );
    const h = holidays.find((h2) => h2.date === dateStr);
    arr.push({ dateStr, dow: d.getDay(), isNonWorking, isToday: dateStr === todayIso, holidayName: h?.name });
  }
  return arr;
}

function useMemoHolidaySet(holidays: HolidayInfo[]) {
  return useMemo(() => new Set(holidays.map((h) => h.date)), [holidays]);
}

function buildDisplayRows(items: ScheduleItem[], showPhases: boolean): DisplayRow[] {
  const sorted = sortScheduleItemsByDate(items);
  if (!showPhases || sorted.length === 0) {
    return sorted.map((item, idx) => ({ type: 'item' as const, item, originalIndex: idx }));
  }

  const rows: DisplayRow[] = [];

  const getGroupKey = (item: ScheduleItem): string => {
    const stage = item.main_stage || item.phase || 'Other';
    if (stage === 'Construction' && item.construction_sub_stage) {
      return `Construction - ${item.construction_sub_stage}`;
    }
    return stage;
  };

  // Group items (preserve date order within each group)
  const groupMap = new Map<string, ScheduleItem[]>();
  for (const item of sorted) {
    const gk = getGroupKey(item);
    const arr = groupMap.get(gk) ?? [];
    arr.push(item);
    groupMap.set(gk, arr);
  }

  // Order groups by earliest scheduled start in each group
  const groupEntries = [...groupMap.entries()].sort(([_, aItems], [__, bItems]) =>
    compareScheduleItemsByDate(aItems[0], bItems[0])
  );

  let originalIndex = 0;
  for (const [gk, groupItems] of groupEntries) {
    rows.push({
      type: 'group',
      groupKey: gk,
      groupName: gk,
      count: groupItems.length,
    });
    for (const item of groupItems) {
      rows.push({ type: 'item', item, originalIndex: originalIndex++ });
    }
  }

  return rows;
}

/* ── component ───────────────────────────────────────────────── */
function ScheduleGanttViewInner({
  items,
  allItems,
  links: allLinks = [],
  showAllLinks = false,
  settings,
  holidays,
  criticalItemIds,
  computedRisk,
  canEdit,
  onEdit,
  onToggleComplete,
  onToggleIncluded,
  onIncludeAll,
  onExcludeAll,
  showExcluded = false,
  isFullscreen = false,
  scale = 'week',
  showPhases = false,
  showCriticalPathOnly = false,
  onRecalcCriticalPath,
  checklistProgressMap = new Map(),
  onOpenCompletionBlock,
  onBarChange,
  onCreateAtDate,
  scheduleUpdating = false,
  listLayout = 'compact',
  showIncludeColumn,
  fillHeight = false,
}: Props, ref: React.Ref<GanttRef>) {
  const { t } = useTranslation();
  const DAY_W = getDayWidth(scale);
  const isCompactList = listLayout === 'compact';
  const includeColumn = showIncludeColumn ?? canEdit;
  const leftGrid = isCompactList
    ? (includeColumn ? GANTT_LEFT_GRID_COMPACT : GANTT_LEFT_GRID_COMPACT_READONLY)
    : GANTT_LEFT_GRID_FULL;
  const leftMinW = isCompactList ? GANTT_LEFT_MIN_W_COMPACT : GANTT_LEFT_MIN_W_FULL;

  const ignoreNextClick = useRef(false);
  const leftBodyRef = useRef<HTMLDivElement>(null);
  const rightBodyRef = useRef<HTMLDivElement>(null);
  const rightHeaderRef = useRef<HTMLDivElement>(null);
  const stickyScrollRef = useRef<HTMLDivElement>(null);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const rightContainerRef = useRef<HTMLDivElement>(null);
  const panStateRef = useRef<{ startX: number; startScrollLeft: number; moved: boolean } | null>(null);
  const isPanningRef = useRef(false);
  const panCleanupRef = useRef<(() => void) | null>(null);
  const resizeStateRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const leftPanelWidthRef = useRef(GANTT_LEFT_MIN_W_COMPACT);

  const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
    if (typeof window === 'undefined') return GANTT_LEFT_MIN_W_COMPACT;
    try {
      const stored = localStorage.getItem(GANTT_LEFT_WIDTH_KEY);
      const n = stored ? Number(stored) : GANTT_LEFT_MIN_W_COMPACT;
      if (!Number.isFinite(n)) return GANTT_LEFT_MIN_W_COMPACT;
      return Math.min(GANTT_LEFT_MAX, Math.max(GANTT_LEFT_MIN, n));
    } catch {
      return GANTT_LEFT_MIN_W_COMPACT;
    }
  });
  leftPanelWidthRef.current = leftPanelWidth;
  const [isPanning, setIsPanning] = useState(false);
  const [isResizingLeft, setIsResizingLeft] = useState(false);

  const rangeStartRef = useRef('');
  const dayCountRef = useRef(0);

  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(600);
  const [timelineScrollWidth, setTimelineScrollWidth] = useState(0);

  const enableDrag = canEdit && scale !== 'month' && scale !== 'year' && !!onBarChange;

  const [dragState, setDragState] = useState<{
    itemId: string;
    mode: 'move' | 'resize-left' | 'resize-right';
    startRelX: number;
    previewStart: string;
    previewEnd: string;
    previewDuration: number;
    hasMoved: boolean;
  } | null>(null);
  const dragStateRef = useRef(dragState);
  dragStateRef.current = dragState;

  const renderStart = Date.now();

  const sortedItems = useMemo(() => sortScheduleItemsByDate(items), [items]);

  const allIncluded = allItems.length > 0 && allItems.every((i) => isScheduleItemIncluded(i));
  const someIncluded = allItems.some((i) => isScheduleItemIncluded(i)) && !allIncluded;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someIncluded;
    }
  }, [someIncluded]);

  // ── Expose scrollToToday via ref ──
  useImperativeHandle(ref, () => ({
    scrollToToday: () => {
      const today = toISODate(new Date());
      const off = dayOffset(today, rangeStartRef.current);
      if (off >= 0 && off < dayCountRef.current && rightBodyRef.current) {
        const targetX = off * DAY_W - (rightBodyRef.current.clientWidth / 2) + (DAY_W / 2);
        rightBodyRef.current.scrollTo({ left: Math.max(0, targetX), behavior: 'smooth' });
      }
    },
  }), [DAY_W]);

  // ── viewport size tracking ──
  useEffect(() => {
    const el = rightBodyRef.current;
    if (!el) return;
    const update = () => {
      setViewportHeight(el.clientHeight);
      setTimelineScrollWidth(el.scrollWidth);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  // Update sticky scrollbar width when timeline changes
  useEffect(() => {
    if (rightBodyRef.current) {
      setTimelineScrollWidth(rightBodyRef.current.scrollWidth);
    }
  });

  // ── Synchronized scroll: left/right vertical + header horizontal + sticky bottom scrollbar ──
  useEffect(() => {
    const left = leftBodyRef.current;
    const right = rightBodyRef.current;
    const header = rightHeaderRef.current;
    const sticky = stickyScrollRef.current;
    if (!left || !right) return;

    let syncingV = false;
    let syncingH = false;

    const syncVertical = (source: HTMLElement, target: HTMLElement) => {
      if (syncingV) return;
      syncingV = true;
      target.scrollTop = source.scrollTop;
      requestAnimationFrame(() => { syncingV = false; });
    };

    const syncHorizontal = (scrollLeft: number, exclude: HTMLElement | null) => {
      if (syncingH) return;
      syncingH = true;
      if (header && header !== exclude) header.scrollLeft = scrollLeft;
      if (sticky && sticky !== exclude) sticky.scrollLeft = scrollLeft;
      requestAnimationFrame(() => { syncingH = false; });
    };

    const onLeftScroll = () => syncVertical(left, right);
    const onRightScroll = () => {
      setScrollTop(right.scrollTop);
      syncVertical(right, left);
      syncHorizontal(right.scrollLeft, right);
    };
    const onHeaderScroll = () => syncHorizontal(header!.scrollLeft, header);
    const onStickyScroll = () => {
      if (syncingH) return;
      syncingH = true;
      right.scrollLeft = sticky!.scrollLeft;
      if (header) header.scrollLeft = sticky!.scrollLeft;
      requestAnimationFrame(() => { syncingH = false; });
    };

    left.addEventListener('scroll', onLeftScroll);
    right.addEventListener('scroll', onRightScroll);
    if (header) header.addEventListener('scroll', onHeaderScroll);
    if (sticky) sticky.addEventListener('scroll', onStickyScroll);

    return () => {
      left.removeEventListener('scroll', onLeftScroll);
      right.removeEventListener('scroll', onRightScroll);
      if (header) header.removeEventListener('scroll', onHeaderScroll);
      if (sticky) sticky.removeEventListener('scroll', onStickyScroll);
    };
  }, [sortedItems.length]);

  // ── date range ──
  const { rangeStart, dayCount } = useMemo(() => {
    const t0 = performance.now();
    const withDates = sortedItems.filter((i) => i.start_date);
    const starts = withDates.map((i) => i.start_date as string);
    const ends = withDates.map((i) => i.end_date).filter(Boolean) as string[];
    if (starts.length === 0) {
      const today = toISODate(new Date());
      rangeStartRef.current = today;
      dayCountRef.current = 30;
      perfLog('dateRange (empty)', performance.now() - t0, { dayCount: 30 });
      return { rangeStart: today, dayCount: 30 };
    }
    const minS = starts.reduce((a, b) => (a < b ? a : b));
    const maxE = ends.length > 0 ? ends.reduce((a, b) => (a > b ? a : b)) : minS;
    const s = new Date(parseISODate(minS));
    s.setDate(s.getDate() - 7);
    const e = new Date(parseISODate(maxE));
    e.setDate(e.getDate() + 28);
    const count = Math.max(30, Math.ceil((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)) + 1);
    const result = { rangeStart: toISODate(s), dayCount: count };
    rangeStartRef.current = result.rangeStart;
    dayCountRef.current = result.dayCount;
    perfLog('dateRange', performance.now() - t0, { dayCount: count, itemCount: sortedItems.length });
    return result;
  }, [sortedItems]);

  const holidayDateSet = useMemoHolidaySet(holidays);

  const dayInfos = useMemo(() => {
    const t0 = performance.now();
    const result = buildDayInfos(rangeStart, dayCount, settings, holidayDateSet, holidays);
    perfLog('dayInfos', performance.now() - t0, { count: result.length });
    return result;
  }, [rangeStart, dayCount, settings, holidayDateSet, holidays]);

  const monthGroups = useMemo(() => {
    const groups: { month: string; startIdx: number; dayCount: number }[] = [];
    let current: { month: string; startIdx: number; dayCount: number } | null = null;
    for (let i = 0; i < dayInfos.length; i++) {
      const d = parseISODate(dayInfos[i].dateStr);
      const month = d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
      if (!current || current.month !== month) {
        if (current) groups.push(current);
        current = { month, startIdx: i, dayCount: 1 };
      } else {
        current.dayCount++;
      }
    }
    if (current) groups.push(current);
    return groups;
  }, [dayInfos]);

  const nonWorkingSpans = useMemo(() => {
    const spans: { start: number; width: number }[] = [];
    let current: { start: number; width: number } | null = null;
    for (let i = 0; i < dayInfos.length; i++) {
      if (dayInfos[i].isNonWorking) {
        if (!current) {
          current = { start: i * DAY_W, width: DAY_W };
        } else {
          current.width += DAY_W;
        }
      } else if (current) {
        spans.push(current);
        current = null;
      }
    }
    if (current) spans.push(current);
    return spans;
  }, [dayInfos]);

  const handleHeaderCheckbox = useCallback(() => {
    if (allIncluded) onExcludeAll();
    else onIncludeAll();
  }, [allIncluded, onIncludeAll, onExcludeAll]);

  const handleRowClick = useCallback((item: ScheduleItem) => {
    setSelectedItemId(item.id);
    onEdit(item);
  }, [onEdit]);

  const getRelX = useCallback((clientX: number) => {
    const el = rightBodyRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    return clientX - rect.left + el.scrollLeft;
  }, []);

  const clickDateFromClientX = useCallback((clientX: number): string => {
    const relX = getRelX(clientX);
    const dayIdx = Math.max(0, Math.floor(relX / DAY_W));
    let date = dateFromIndex(dayIdx, rangeStart);
    date = shiftToWorkingDay(date, settings, holidays);
    return date;
  }, [getRelX, DAY_W, rangeStart, settings, holidays]);

  const handleTimelineClick = useCallback((e: React.MouseEvent) => {
    if (!canEdit || !onCreateAtDate) return;
    if (ignoreNextClick.current) {
      ignoreNextClick.current = false;
      return;
    }
    e.stopPropagation();
    onCreateAtDate(clickDateFromClientX(e.clientX));
  }, [canEdit, onCreateAtDate, clickDateFromClientX]);

  const handleHeaderDayClick = useCallback((dateStr: string) => {
    if (!canEdit || !onCreateAtDate) return;
    if (ignoreNextClick.current) {
      ignoreNextClick.current = false;
      return;
    }
    onCreateAtDate(shiftToWorkingDay(dateStr, settings, holidays));
  }, [canEdit, onCreateAtDate, settings, holidays]);

  const setPanActive = useCallback((active: boolean) => {
    isPanningRef.current = active;
    setIsPanning(active);
    const el = rightBodyRef.current;
    if (el) {
      el.classList.toggle(GANTT_PAN_ACTIVE_CLASS, active);
    }
  }, []);

  const endPan = useCallback(() => {
    panCleanupRef.current?.();
    panCleanupRef.current = null;

    if (panStateRef.current?.moved) {
      ignoreNextClick.current = true;
    }
    panStateRef.current = null;
    setPanActive(false);
    document.body.classList.remove('jph-gantt-panning', GANTT_PAN_ACTIVE_CLASS);
  }, [setPanActive]);

  const attachPanListeners = useCallback(() => {
    panCleanupRef.current?.();
    panCleanupRef.current = null;

    const onMove = (e: PointerEvent | MouseEvent) => {
      if (!panStateRef.current || !rightBodyRef.current) return;
      const delta = e.clientX - panStateRef.current.startX;
      if (Math.abs(delta) > PAN_THRESHOLD_PX) {
        panStateRef.current.moved = true;
        if (!isPanningRef.current) {
          setPanActive(true);
        }
      }
      if (panStateRef.current.moved) {
        rightBodyRef.current.scrollLeft = panStateRef.current.startScrollLeft - delta;
      }
    };

    const onEnd = () => {
      endPan();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        endPan();
      }
    };

    document.addEventListener('pointermove', onMove, { capture: true });
    document.addEventListener('pointerup', onEnd, { capture: true });
    document.addEventListener('pointercancel', onEnd, { capture: true });
    window.addEventListener('mouseup', onEnd, { capture: true });
    window.addEventListener('touchend', onEnd, { capture: true });
    window.addEventListener('touchcancel', onEnd, { capture: true });
    window.addEventListener('blur', onEnd);
    document.addEventListener('visibilitychange', onVisibilityChange);

    panCleanupRef.current = () => {
      document.removeEventListener('pointermove', onMove, { capture: true });
      document.removeEventListener('pointerup', onEnd, { capture: true });
      document.removeEventListener('pointercancel', onEnd, { capture: true });
      window.removeEventListener('mouseup', onEnd, { capture: true });
      window.removeEventListener('touchend', onEnd, { capture: true });
      window.removeEventListener('touchcancel', onEnd, { capture: true });
      window.removeEventListener('blur', onEnd);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [endPan, setPanActive]);

  useEffect(() => () => {
    endPan();
  }, [endPan]);

  useEffect(() => {
    endPan();
  }, [isFullscreen, endPan]);

  const handleChartPanStart = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (dragStateRef.current) return;
    if (isPanBlockedTarget(e.target)) return;
    const right = rightBodyRef.current;
    if (!right) return;
    if (isNearChartScrollbar(e.clientX, e.clientY, right)) return;

    endPan();

    panStateRef.current = { startX: e.clientX, startScrollLeft: right.scrollLeft, moved: false };
    attachPanListeners();
  }, [attachPanListeners, endPan]);

  useEffect(() => {
    if (!isResizingLeft) return;

    const onMove = (e: MouseEvent) => {
      if (resizeStateRef.current) {
        const delta = e.clientX - resizeStateRef.current.startX;
        const next = Math.min(GANTT_LEFT_MAX, Math.max(GANTT_LEFT_MIN, resizeStateRef.current.startWidth + delta));
        leftPanelWidthRef.current = next;
        setLeftPanelWidth(next);
      }
    };

    const onUp = () => {
      if (resizeStateRef.current) {
        try {
          localStorage.setItem(GANTT_LEFT_WIDTH_KEY, String(leftPanelWidthRef.current));
        } catch {
          /* ignore */
        }
      }
      resizeStateRef.current = null;
      setIsResizingLeft(false);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isResizingLeft]);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resizeStateRef.current = { startX: e.clientX, startWidth: leftPanelWidth };
    setIsResizingLeft(true);
  }, [leftPanelWidth]);

  const computeDragPreview = useCallback((
    item: ScheduleItem,
    mode: 'move' | 'resize-left' | 'resize-right',
    deltaDays: number
  ): { start: string; end: string; duration: number } => {
    const origStart = item.start_date!;
    const origEnd = item.end_date!;

    if (mode === 'move') {
      const origStartIdx = dayOffset(origStart, rangeStart);
      const newStartIdx = origStartIdx + deltaDays;
      let newStart = dateFromIndex(newStartIdx, rangeStart);
      newStart = shiftToWorkingDay(newStart, settings, holidays);
      const newEnd = calculateEndDate(newStart, item.duration_days, settings, holidays);
      return { start: newStart, end: newEnd, duration: item.duration_days };
    }

    if (mode === 'resize-right') {
      const origEndIdx = dayOffset(origEnd, rangeStart);
      const newEndIdx = origEndIdx + deltaDays;
      let newEnd = dateFromIndex(newEndIdx, rangeStart);
      newEnd = shiftToWorkingDay(newEnd, settings, holidays);
      const newDuration = countWorkingDaysInclusive(origStart, newEnd, settings, holidays);
      return { start: origStart, end: newEnd, duration: newDuration };
    }

    const origStartIdx = dayOffset(origStart, rangeStart);
    const newStartIdx = origStartIdx + deltaDays;
    let newStart = dateFromIndex(newStartIdx, rangeStart);
    newStart = shiftToWorkingDay(newStart, settings, holidays);
    let newEnd = origEnd;
    if (parseISODate(newStart) > parseISODate(newEnd)) {
      newEnd = calculateEndDate(newStart, 1, settings, holidays);
    }
    const newDuration = countWorkingDaysInclusive(newStart, newEnd, settings, holidays);
    return { start: newStart, end: newEnd, duration: newDuration };
  }, [rangeStart, settings, holidays]);

  useEffect(() => {
    if (!dragState) return;

    const handleMove = (clientX: number) => {
      const relX = getRelX(clientX);
      const deltaPx = relX - dragState.startRelX;
      const deltaDays = Math.round(deltaPx / DAY_W);

      const item = sortedItems.find((i) => i.id === dragState.itemId);
      if (!item || !item.start_date || !item.end_date) return;

      const preview = computeDragPreview(item, dragState.mode, deltaDays);
      setDragState((prev) =>
        prev
          ? {
              ...prev,
              previewStart: preview.start,
              previewEnd: preview.end,
              previewDuration: preview.duration,
              hasMoved: prev.hasMoved || Math.abs(deltaPx) > 3,
            }
          : null
      );
    };

    const onMouseMove = (e: MouseEvent) => handleMove(e.clientX);
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches[0]) handleMove(e.touches[0].clientX);
    };

    const onUp = () => {
      setDragState((prev) => {
        if (!prev || !onBarChange) return null;
        const item = sortedItems.find((i) => i.id === prev.itemId);
        if (!item) return null;
        if (prev.hasMoved) {
          ignoreNextClick.current = true;
          onBarChange({
            item,
            mode: prev.mode,
            newStart: prev.previewStart,
            newEnd: prev.previewEnd,
            newDuration: prev.previewDuration,
          });
        }
        return null;
      });
    };

    const onCancel = () => {
      setDragState(null);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        onCancel();
      }
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onUp);
    window.addEventListener('touchcancel', onCancel);
    window.addEventListener('blur', onCancel);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
      window.removeEventListener('touchcancel', onCancel);
      window.removeEventListener('blur', onCancel);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [dragState, sortedItems, DAY_W, getRelX, computeDragPreview, onBarChange]);

  const handleBarMouseDown = useCallback((
    e: React.MouseEvent | React.TouchEvent | React.PointerEvent,
    item: ScheduleItem
  ) => {
    e.stopPropagation();
    if (!enableDrag || !item.start_date || !item.end_date) return;
    endPan();

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const relX = getRelX(clientX);
    const startIdx = dayOffset(item.start_date, rangeStart);
    const endIdx = dayOffset(item.end_date, rangeStart);
    const barLeft = startIdx * DAY_W;
    const barWidth = (endIdx - startIdx + 1) * DAY_W;
    const clickRelX = relX - barLeft;

    let mode: 'move' | 'resize-left' | 'resize-right' = 'move';
    if (clickRelX < 10) mode = 'resize-left';
    else if (clickRelX > barWidth - 10) mode = 'resize-right';

    setDragState({
      itemId: item.id,
      mode,
      startRelX: relX,
      previewStart: item.start_date,
      previewEnd: item.end_date,
      previewDuration: item.duration_days,
      hasMoved: false,
    });
  }, [enableDrag, DAY_W, rangeStart, getRelX, endPan]);

  const handleBarClick = useCallback((e: React.MouseEvent, item: ScheduleItem) => {
    e.stopPropagation();
    if (ignoreNextClick.current) {
      ignoreNextClick.current = false;
      return;
    }
    handleRowClick(item);
  }, [handleRowClick]);

  // ── Build display rows with phase grouping ──
  const displayRows = useMemo(() => buildDisplayRows(sortedItems, showPhases), [sortedItems, showPhases]);

  const getRowHeight = (row: DisplayRow) => (row.type === 'group' ? GROUP_ROW_HEIGHT : ROW_HEIGHT);

  const { rowOffsets, totalBodyHeight } = useMemo(() => {
    const offsets: number[] = [];
    let offset = 0;
    for (const row of displayRows) {
      offsets.push(offset);
      offset += getRowHeight(row);
    }
    return { rowOffsets: offsets, totalBodyHeight: offset };
  }, [displayRows]);

  // ── dependency computations ──
  const itemIndexMap = useMemo(() => {
    const m = new Map<string, number>();
    displayRows.forEach((row, idx) => {
      if (row.type === 'item') m.set(row.item.id, idx);
    });
    return m;
  }, [displayRows]);

  const barPositions = useMemo(() => {
    const m = new Map<string, { left: number; width: number }>();
    sortedItems.forEach((item) => {
      if (!item.start_date) return;
      const sOff = dayOffset(item.start_date, rangeStart);
      const eOff = item.end_date ? dayOffset(item.end_date, rangeStart) : sOff;
      const left = Math.max(0, sOff * DAY_W);
      const width = Math.max(DAY_W, (eOff - sOff + 1) * DAY_W);
      m.set(item.id, { left, width });
    });
    return m;
  }, [sortedItems, rangeStart, DAY_W]);

  const visibleLinks = useMemo(() => {
    return allLinks.filter((link) => {
      const predIdx = itemIndexMap.get(link.predecessor_item_id);
      const succIdx = itemIndexMap.get(link.successor_item_id);
      return predIdx !== undefined && succIdx !== undefined;
    });
  }, [allLinks, itemIndexMap]);

  const itemsWithLinks = useMemo(() => {
    const set = new Set<string>();
    for (const link of visibleLinks) {
      set.add(link.predecessor_item_id);
      set.add(link.successor_item_id);
    }
    return set;
  }, [visibleLinks]);

  const linkInfoByItem = useMemo(() => {
    const map = new Map<
      string,
      {
        asPred: { linkId: string; linkType: string; succId: string }[];
        asSucc: { linkId: string; linkType: string; predId: string }[];
        hasHiddenLinks: boolean;
      }
    >();
    for (const row of displayRows) {
      if (row.type === 'item') {
        map.set(row.item.id, { asPred: [], asSucc: [], hasHiddenLinks: false });
      }
    }
    for (const link of visibleLinks) {
      const predInfo = map.get(link.predecessor_item_id);
      const succInfo = map.get(link.successor_item_id);
      if (predInfo) predInfo.asPred.push({ linkId: link.id, linkType: link.link_type, succId: link.successor_item_id });
      if (succInfo) succInfo.asSucc.push({ linkId: link.id, linkType: link.link_type, predId: link.predecessor_item_id });
    }
    for (const link of allLinks) {
      const predVisible = itemIndexMap.has(link.predecessor_item_id);
      const succVisible = itemIndexMap.has(link.successor_item_id);
      if (predVisible && !succVisible) {
        const info = map.get(link.predecessor_item_id);
        if (info) info.hasHiddenLinks = true;
      }
      if (succVisible && !predVisible) {
        const info = map.get(link.successor_item_id);
        if (info) info.hasHiddenLinks = true;
      }
    }
    return map;
  }, [displayRows, visibleLinks, allLinks, itemIndexMap]);

  const focusItemId = hoveredRow || selectedItemId;
  // Fix 9: Dependency lines are ALWAYS visible — no need to filter by hover/selection
  const activeLinkIds = useMemo(() => {
    const set = new Set<string>();
    // Always show all visible links
    for (const link of visibleLinks) set.add(link.id);
    return set;
  }, [visibleLinks]);

  const svgPaths = useMemo(() => {
    const t0 = performance.now();
    const paths: { id: string; d: string; linkType: string }[] = [];

    for (const link of visibleLinks) {
      // Fix 9: Always show all dependency lines (activeLinkIds always contains all visible links)
      const predIdx = itemIndexMap.get(link.predecessor_item_id);
      const succIdx = itemIndexMap.get(link.successor_item_id);
      if (predIdx === undefined || succIdx === undefined) continue;

      const predPos = barPositions.get(link.predecessor_item_id);
      const succPos = barPositions.get(link.successor_item_id);
      if (!predPos || !succPos) continue;

      const predY = rowOffsets[predIdx] + ROW_HEIGHT / 2;
      const succY = rowOffsets[succIdx] + ROW_HEIGHT / 2;

      let predX: number;
      let succX: number;

      if (link.link_type === 'SS') {
        predX = predPos.left;
        succX = succPos.left;
      } else {
        predX = predPos.left + predPos.width;
        succX = succPos.left;
      }

      const midX = (predX + succX) / 2;
      const d = `M ${predX} ${predY} L ${midX} ${predY} L ${midX} ${succY} L ${succX} ${succY}`;

      paths.push({ id: link.id, d, linkType: link.link_type });
    }
    perfLog('svgPaths', performance.now() - t0, {
      computed: paths.length,
      visibleLinks: visibleLinks.length,
    });
    return paths;
  }, [visibleLinks, itemIndexMap, barPositions, activeLinkIds, showAllLinks, rowOffsets]);

  const activeNodes = useMemo(() => {
    const nodes: {
      key: string;
      x: number;
      y: number;
      isActive: boolean;
      itemId: string;
    }[] = [];
    for (const link of visibleLinks) {
      const predIdx = itemIndexMap.get(link.predecessor_item_id);
      const succIdx = itemIndexMap.get(link.successor_item_id);
      if (predIdx === undefined || succIdx === undefined) continue;
      const predPos = barPositions.get(link.predecessor_item_id);
      const succPos = barPositions.get(link.successor_item_id);
      if (!predPos || !succPos) continue;

      const isActive = activeLinkIds.has(link.id);
      const predY = rowOffsets[predIdx] + ROW_HEIGHT / 2;
      const succY = rowOffsets[succIdx] + ROW_HEIGHT / 2;
      const predX = link.link_type === 'SS' ? predPos.left : predPos.left + predPos.width;
      const succX = link.link_type === 'SS' ? succPos.left : succPos.left;

      nodes.push({ key: `pred-${link.id}`, x: predX, y: predY, isActive, itemId: link.predecessor_item_id });
      nodes.push({ key: `succ-${link.id}`, x: succX, y: succY, isActive, itemId: link.successor_item_id });
    }
    return nodes;
  }, [visibleLinks, itemIndexMap, barPositions, activeLinkIds, rowOffsets]);

  // Fix 9: Always render SVG dependency lines
  const shouldRenderSvg = visibleLinks.length > 0;

  // ── virtualization (use actual row offsets — fixes missing titles on late rows e.g. task 156) ──
  const visibleRange = useMemo(() => {
    if (displayRows.length === 0) return { startIdx: 0, endIdx: 0 };

    const viewTop = scrollTop;
    const viewBottom = scrollTop + viewportHeight;

    let startIdx = 0;
    for (let i = 0; i < displayRows.length; i++) {
      const rowBottom = rowOffsets[i] + getRowHeight(displayRows[i]);
      if (rowBottom >= viewTop) {
        startIdx = Math.max(0, i - VIRTUAL_BUFFER);
        break;
      }
    }

    let endIdx = displayRows.length - 1;
    for (let i = startIdx; i < displayRows.length; i++) {
      if (rowOffsets[i] > viewBottom) {
        endIdx = Math.min(displayRows.length - 1, i + VIRTUAL_BUFFER);
        break;
      }
    }

    return { startIdx, endIdx };
  }, [scrollTop, viewportHeight, displayRows, rowOffsets]);

  useEffect(() => {
    perfLog('render', Date.now() - renderStart, {
      visibleRows: visibleRange.endIdx - visibleRange.startIdx + 1,
      totalRows: displayRows.length,
      svgPaths: svgPaths.length,
    });
  }, [visibleRange.endIdx, visibleRange.startIdx, displayRows.length, svgPaths.length]);

  const timelineWidth = dayCount * DAY_W;

  const todayOff = dayOffset(toISODate(new Date()), rangeStart);
  const showTodayLine = todayOff >= 0 && todayOff < dayCount;

  // ── Shift+Wheel scrolls horizontally on the chart ──
  const handleWheelOnChart = useCallback((e: React.WheelEvent) => {
    const right = rightBodyRef.current;
    if (!right) return;
    if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      e.preventDefault();
      right.scrollLeft += e.deltaX || e.deltaY;
    }
  }, []);

  const noCriticalItems = showCriticalPathOnly && criticalItemIds.size === 0;

  // ── Compute the viewport height for the Gantt chart body ──
  // In fullscreen: use (screen height - toolbar height)
  // In normal mode: use a calc that accounts for the tab bar + toolbar + padding inside the page
  // We keep a reasonable min so short schedules still look good.
  const ganttContainerHeight = fillHeight
    ? '100%'
    : isFullscreen
      ? 'calc(100vh - 56px)'
      : 'calc(100vh - 220px)';

  return (
    <div
      className={`flex flex-col bg-white border-t border-gray-200 min-w-0 relative ${dragState ? 'select-none' : ''}`}
      style={{ height: ganttContainerHeight, minHeight: fillHeight ? 480 : 360 }}
    >
      {scheduleUpdating && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[70] bg-white/95 border border-gray-200 rounded-full px-3 py-1.5 shadow-sm flex items-center gap-2 pointer-events-none">
          <i className="ri-loader-4-line animate-spin text-[#E8621A] text-sm"></i>
          <span className="text-[10px] font-semibold text-[#374151]">{t('ganttView.updatingSchedule', { defaultValue: 'Updating schedule…' })}</span>
        </div>
      )}

      {/* Read-only warning for Month/Year scale */}
      {(scale === 'month' || scale === 'year') && (
        <div className="absolute top-1 right-1 z-[60] bg-[#FEF0E7] border border-[#FDBA74] rounded-lg px-3 py-1.5 shadow-sm pointer-events-none">
          <p className="text-[10px] font-bold text-[#92400E] flex items-center gap-1">
            <i className="ri-information-line"></i>
            {t('ganttView.useWeekView')}
          </p>
        </div>
      )}

      {/* Main layout: left + right side by side, fills remaining height */}
      <div className="flex flex-1 min-h-0 overflow-hidden">

        {/* LEFT PANEL — fixed width, hides own horizontal scrollbar */}
        <div
          ref={leftBodyRef}
          className="flex-shrink-0 border-r border-gray-200 scrollbar-hide-h"
          style={{
            width: leftPanelWidth,
            minWidth: leftMinW,
            maxWidth: GANTT_LEFT_MAX,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {/* Sticky header row */}
          <div
            className="sticky top-0 z-30 border-b border-gray-200 bg-[#F9FAFB] grid items-center text-[10px] font-bold text-[#6B7280] uppercase tracking-wider select-none flex-shrink-0"
            style={{ height: HEADER_H, gridTemplateColumns: leftGrid }}
          >
            {includeColumn && (
              <div className="flex items-center justify-center">
                <input
                  ref={headerCheckboxRef}
                  type="checkbox"
                  checked={allIncluded}
                  onChange={handleHeaderCheckbox}
                  className="w-3.5 h-3.5 accent-[#E8621A] cursor-pointer"
                  title={allIncluded ? t('ganttView.excludeAll') : t('ganttView.includeAll')}
                />
              </div>
            )}
            {!isCompactList && <div className="text-center">No.</div>}
            <div className="min-w-0 px-1 truncate">Title</div>
            {!isCompactList && <div className="text-center">Dur</div>}
            <div className="text-center">{isCompactList ? 'Start' : 'Start'}</div>
            {!isCompactList && (
              <>
                <div className="text-center">End</div>
                <div className="text-center">%</div>
                <div className="text-center">✓</div>
              </>
            )}
            {isCompactList && <div className="text-center">Days</div>}
            {isCompactList && <div className="text-center">✓</div>}
          </div>

          <div style={{ minHeight: totalBodyHeight }}>
            {displayRows.map((row, idx) => {
              if (row.type === 'group') {
                if (idx < visibleRange.startIdx || idx > visibleRange.endIdx) {
                  return (
                    <div
                      key={`group-${row.groupKey}-${idx}`}
                      style={{ height: GROUP_ROW_HEIGHT, boxSizing: 'border-box' }}
                    />
                  );
                }
                return (
                  <div
                    key={`group-${row.groupKey}-${idx}`}
                    className="flex items-center bg-[#F3F4F6] border-b border-gray-200 text-[10px] font-bold text-[#6B7280] uppercase tracking-wider"
                    style={{ height: GROUP_ROW_HEIGHT, boxSizing: 'border-box', gridColumn: '1 / -1' }}
                  >
                    <div className="flex-1 min-w-0 px-3 flex items-center gap-2">
                      <i className="ri-stack-line text-[#9CA3AF]"></i>
                      <span className="truncate">{row.groupName}</span>
                      <span className="flex-shrink-0 text-[9px] text-[#9CA3AF] bg-gray-100 px-1 rounded">
                        {row.count}
                      </span>
                    </div>
                  </div>
                );
              }

              const item = row.item;
              if (idx < visibleRange.startIdx || idx > visibleRange.endIdx) {
                return (
                  <div
                    key={item.id}
                    className="border-b border-gray-100"
                    style={{ height: ROW_HEIGHT, boxSizing: 'border-box' }}
                  />
                );
              }

              const displayTitle = getTaskDisplayTitle(item);

              const isHovered = hoveredRow === item.id;
              const isExcluded = item.is_included === false;
              const status = getStatusColor(item.progress, item.is_complete);

              return (
                <div
                  key={item.id}
                  className={`grid items-center text-[11px] border-b border-gray-100 transition-colors cursor-pointer ${
                    isHovered ? 'bg-[#FEF0E7]/50' : row.originalIndex % 2 === 0 ? 'bg-white' : 'bg-[#FAFAFA]'
                  } ${isExcluded ? 'opacity-40' : ''}`}
                  style={{ height: ROW_HEIGHT, boxSizing: 'border-box', gridTemplateColumns: leftGrid }}
                  onMouseEnter={() => setHoveredRow(item.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  onClick={() => handleRowClick(item)}
                >
                  {includeColumn && (
                    <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={item.is_included}
                        onChange={(e) => onToggleIncluded(item, e.target.checked)}
                        className="w-3.5 h-3.5 accent-[#E8621A] cursor-pointer"
                      />
                    </div>
                  )}
                  {!isCompactList && (
                    <div className="text-center text-[#9CA3AF] font-mono text-[10px]">
                      {item.task_no ?? row.originalIndex + 1}
                    </div>
                  )}
                  <div className="min-w-0 px-1 overflow-hidden">
                    <div className="flex items-center gap-1 min-w-0">
                      {!isExcluded && criticalItemIds.has(item.id) && (
                        <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#EF4444]" title={t('ganttView.criticalPath')} />
                      )}
                      <span
                        className={`truncate font-medium text-[12px] block min-w-0 ${isExcluded ? 'text-[#9CA3AF]' : 'text-[#1A1A1A]'}`}
                        title={displayTitle}
                      >
                        {displayTitle}
                      </span>
                      {isExcluded && (
                        <span className="flex-shrink-0 text-[8px] text-[#9CA3AF] bg-gray-100 px-1 rounded">{t('ganttView.excluded')}</span>
                      )}
                    </div>
                  </div>
                  {!isCompactList && (
                    <div className={`text-center text-[10px] ${isExcluded ? 'text-[#9CA3AF]' : 'text-[#6B7280]'}`}>
                      {item.duration_days}d
                    </div>
                  )}
                  <div className={`text-center text-[10px] truncate px-0.5 ${isExcluded ? 'text-[#9CA3AF]' : 'text-[#374151]'}`} title={item.start_date ? formatDateLabel(item.start_date) : undefined}>
                    {item.start_date ? formatDateLabel(item.start_date) : '—'}
                  </div>
                  {!isCompactList && (
                    <>
                      <div className={`text-center text-[10px] truncate px-0.5 ${isExcluded ? 'text-[#9CA3AF]' : 'text-[#374151]'}`} title={item.end_date ? formatDateLabel(item.end_date) : undefined}>
                        {item.end_date ? formatDateLabel(item.end_date) : '—'}
                      </div>
                      <div className="text-center flex items-center justify-center">
                        {!isExcluded && (
                          <>
                            {(() => {
                              const cp = checklistProgressMap.get(item.id);
                              const ganttPct = cp?.hasLinkedItems ? cp.progressPct : item.progress;
                              return (
                                <span className="text-[9px] font-bold px-1 py-0.5 rounded-sm" style={{ backgroundColor: status.bg, color: status.text }}>
                                  {ganttPct}%
                                </span>
                              );
                            })()}
                          </>
                        )}
                      </div>
                      <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                        {canEdit ? (
                          <input
                            type="checkbox"
                            checked={item.is_complete}
                            disabled={isExcluded}
                            onChange={(e) => {
                              const cp = checklistProgressMap.get(item.id);
                              if (e.target.checked && cp?.hasLinkedItems && onOpenCompletionBlock) {
                                onOpenCompletionBlock(item);
                                return;
                              }
                              onToggleComplete(item, e.target.checked);
                            }}
                            className="w-3.5 h-3.5 accent-[#E8621A] cursor-pointer disabled:opacity-30"
                          />
                        ) : (
                          <div className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center mx-auto ${item.is_complete ? 'bg-[#059669] border-[#059669]' : 'border-gray-300'}`}>
                            {item.is_complete && <i className="ri-check-line text-white text-[7px]"></i>}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  {isCompactList && (
                    <>
                      <div className={`text-center text-[10px] ${isExcluded ? 'text-[#9CA3AF]' : 'text-[#6B7280]'}`}>
                        {item.duration_days}d
                      </div>
                      <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                        {canEdit ? (
                          <input
                            type="checkbox"
                            checked={item.is_complete}
                            disabled={isExcluded}
                            onChange={(e) => {
                              const cp = checklistProgressMap.get(item.id);
                              if (e.target.checked && cp?.hasLinkedItems && onOpenCompletionBlock) {
                                onOpenCompletionBlock(item);
                                return;
                              }
                              onToggleComplete(item, e.target.checked);
                            }}
                            className="w-3.5 h-3.5 accent-[#E8621A] cursor-pointer disabled:opacity-30"
                          />
                        ) : (
                          <div className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center mx-auto ${item.is_complete ? 'bg-[#059669] border-[#059669]' : 'border-gray-300'}`}>
                            {item.is_complete && <i className="ri-check-line text-white text-[7px]"></i>}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Draggable divider between task table and chart */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize task list"
          onMouseDown={handleResizeStart}
          className={`flex-shrink-0 w-1.5 cursor-col-resize hover:bg-[#E8621A]/30 transition-colors ${isResizingLeft ? 'bg-[#E8621A]/40' : 'bg-gray-200'}`}
        />

        {/* RIGHT PANEL — scrolls both X and Y; header is sticky inside */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Sticky date header — scrolls horizontally but not vertically */}
          <div
            ref={rightHeaderRef}
            className="flex-shrink-0 overflow-x-hidden overflow-y-hidden scrollbar-hide"
            style={{ height: HEADER_H, borderBottom: '1px solid #E5E7EB' }}
          >
            <div style={{ width: timelineWidth, minWidth: '100%' }}>
              <div className="bg-white" style={{ height: HEADER_H }}>
                <div className="flex border-b border-gray-200" style={{ height: 24 }}>
                  {monthGroups.map((mg) => (
                    <div
                      key={`${mg.month}-${mg.startIdx}`}
                      className="text-[10px] font-bold text-[#374151] uppercase tracking-wider px-1 flex items-center border-r border-gray-200 bg-[#F9FAFB] whitespace-nowrap overflow-hidden"
                      style={{ width: mg.dayCount * DAY_W, height: 24 }}
                    >
                      {mg.month}
                    </div>
                  ))}
                </div>
                <div className="flex" style={{ height: 24 }}>
                  {dayInfos.map((di) => {
                    const dayLabel = getDayLabel(scale, di.dateStr);
                    const shortLabel = getShortDayLabel(scale, di.dateStr);
                    return (
                      <div
                        key={di.dateStr}
                        data-gantt-interactive
                        className={`flex-shrink-0 border-r border-gray-100 text-center flex flex-col items-center justify-center ${di.isNonWorking ? 'bg-[#F3F4F6]' : di.isToday ? 'bg-[#FEF2F2]' : ''} ${canEdit && onCreateAtDate ? 'cursor-pointer hover:bg-[#FEF0E7]/60' : ''}`}
                        style={{ width: DAY_W, height: 24 }}
                        title={canEdit && onCreateAtDate ? t('ganttView.addTaskOnDate', { defaultValue: 'Click to add task' }) : undefined}
                        onClick={() => handleHeaderDayClick(di.dateStr)}
                      >
                        <div className={`text-[10px] font-bold leading-none ${di.isToday ? 'text-[#EF4444]' : di.isNonWorking ? 'text-[#9CA3AF]' : 'text-[#374151]'}`}>
                          {dayLabel}
                        </div>
                        {shortLabel && (
                          <div className={`text-[8px] leading-none mt-0.5 ${di.isNonWorking ? 'text-[#9CA3AF]' : 'text-[#6B7280]'}`}>
                            {shortLabel}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Scrollable chart body — scrolls both X and Y */}
          <div
            ref={rightBodyRef}
            data-gantt-pan-surface
            className={`flex-1 overflow-x-auto overflow-y-auto min-h-0 min-w-0 ${GANTT_PAN_SURFACE_CLASS} ${isPanning ? GANTT_PAN_ACTIVE_CLASS : ''}`}
            onWheel={handleWheelOnChart}
            onPointerDown={handleChartPanStart}
          >
            <div ref={rightContainerRef} className="relative" style={{ width: timelineWidth, minWidth: '100%', minHeight: totalBodyHeight }}>
              {/* Critical path empty state */}
              {noCriticalItems && (
                <div
                  className="absolute inset-0 z-50 flex items-center justify-center bg-white/80"
                  style={{ minHeight: totalBodyHeight }}
                >
                  <div className="text-center px-6 py-8 rounded-2xl bg-white border border-gray-200 shadow-lg max-w-sm">
                    <div className="w-12 h-12 rounded-xl bg-[#FEF2F2] flex items-center justify-center mx-auto mb-3">
                      <i className="ri-route-line text-[#EF4444] text-xl"></i>
                    </div>
                    <h3 className="text-sm font-bold text-[#1A1A1A] mb-1">{t('ganttView.criticalPathNotCalc')}</h3>
                    <p className="text-xs text-[#9CA3AF] mb-4">
                      {t('ganttView.criticalPathDesc')}
                    </p>
                    {onRecalcCriticalPath && (
                      <button
                        type="button"
                        data-gantt-interactive
                        onClick={onRecalcCriticalPath}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#E8621A] text-white text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-[#B84C10] transition-colors"
                      >
                        <i className="ri-route-line"></i>
                        {t('ganttView.calculateCriticalPath')}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {nonWorkingSpans.map((span, i) => (
                <div
                  key={`nw-${i}`}
                  className="absolute top-0 bottom-0 bg-[#F3F4F6]/40 pointer-events-none"
                  style={{ left: span.start, width: span.width }}
                />
              ))}

              {showTodayLine && (
                <div
                  className="absolute top-0 bottom-0 w-px bg-[#EF4444]/70 z-10 pointer-events-none"
                  style={{ left: todayOff * DAY_W + DAY_W / 2 }}
                >
                  <span className="absolute -top-0 text-[7px] font-bold text-[#EF4444] bg-white px-0.5 whitespace-nowrap -translate-x-1/2">
                    {t('ganttView.today')}
                  </span>
                </div>
              )}

              <div
                className="absolute inset-0 pointer-events-none z-[1]"
                style={{
                  backgroundImage: `repeating-linear-gradient(
                    to right,
                    transparent,
                    transparent ${DAY_W - 1}px,
                    rgba(0,0,0,0.025) ${DAY_W - 1}px,
                    rgba(0,0,0,0.025) ${DAY_W}px
                  )`,
                  backgroundSize: `${timelineWidth}px 100%`,
                }}
              />

              {shouldRenderSvg && (
                <svg
                  className="absolute inset-0 z-[5]"
                  style={{ width: timelineWidth, height: totalBodyHeight, pointerEvents: 'none' }}
                >
                  {svgPaths.map((p) => {
                    const isHighlighted = (hoveredRow || selectedItemId) &&
                      visibleLinks.find(l => l.id === p.id) &&
                      (() => {
                        const link = visibleLinks.find(l => l.id === p.id);
                        return link && (link.predecessor_item_id === (hoveredRow || selectedItemId) || link.successor_item_id === (hoveredRow || selectedItemId));
                      })();
                    return (
                      <path
                        key={p.id}
                        d={p.d}
                        fill="none"
                        stroke={isHighlighted ? LINK_LINE_ACTIVE : LINK_LINE_COLOR}
                        strokeWidth={isHighlighted ? 1 : 0.75}
                        strokeDasharray={isHighlighted ? '3 2' : '2 4'}
                        opacity={isHighlighted ? 0.45 : 0.16}
                      />
                    );
                  })}
                  {activeNodes.map((node) => {
                    const isActive = node.isActive;
                    const size = isActive ? NODE_SIZE + 1 : NODE_SIZE;
                    return (
                      <rect
                        key={node.key}
                        x={node.x - size / 2}
                        y={node.y - size / 2}
                        width={size}
                        height={size}
                        rx={1}
                        fill={LINK_LINE_COLOR}
                        opacity={isActive ? 0.35 : 0.2}
                        stroke="white"
                        strokeWidth={0.5}
                      />
                    );
                  })}
                </svg>
              )}

              {displayRows.map((row, rowIdx) => {
                if (row.type === 'group') {
                  if (rowIdx < visibleRange.startIdx || rowIdx > visibleRange.endIdx) {
                    return (
                      <div
                        key={`group-right-${row.groupKey}-${rowIdx}`}
                        className="relative border-b border-gray-200 bg-[#F3F4F6]"
                        style={{ height: GROUP_ROW_HEIGHT, boxSizing: 'border-box' }}
                      />
                    );
                  }
                  return (
                    <div
                      key={`group-right-${row.groupKey}-${rowIdx}`}
                      className="relative border-b border-gray-200 bg-[#F3F4F6] flex items-center"
                      style={{ height: GROUP_ROW_HEIGHT, boxSizing: 'border-box' }}
                    >
                      <span className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider px-3">
                        {row.groupName}
                      </span>
                    </div>
                  );
                }

                const item = row.item;
                if (rowIdx < visibleRange.startIdx || rowIdx > visibleRange.endIdx) {
                  return (
                    <div
                      key={item.id}
                      className="relative border-b border-gray-100"
                      style={{ height: ROW_HEIGHT, boxSizing: 'border-box' }}
                    />
                  );
                }

                const displayTitle = getTaskDisplayTitle(item);

                const risk = computedRisk.get(item.id);
                const isCritical = criticalItemIds.has(item.id);
                const isDelayed = risk?.delay_status === 'delayed' || risk?.delay_status === 'completed_late';
                const isAtRisk = risk?.delay_status === 'at_risk';
                const isComplete = item.is_complete || item.progress >= 100;
                const isHovered = hoveredRow === item.id;
                const isExcluded = item.is_included === false;
                const isActive = isHovered || selectedItemId === item.id;
                const linkInfo = linkInfoByItem.get(item.id);
                const hasHiddenLinks = linkInfo?.hasHiddenLinks ?? false;

                const barColor = getBarColor(item);
                const barLabelColor = getContrastColor(barColor);

                const cpGantt = checklistProgressMap.get(item.id);
                const ganttPct = (cpGantt?.hasLinkedItems ? (cpGantt.progressPct / 100) : (item.progress / 100));

                if (isExcluded || !item.start_date) {
                  return (
                    <div
                      key={item.id}
                      className="relative border-b border-gray-100 flex items-center"
                      style={{ height: ROW_HEIGHT, boxSizing: 'border-box' }}
                      onMouseEnter={() => setHoveredRow(item.id)}
                      onMouseLeave={() => setHoveredRow(null)}
                    >
                      <span className="text-[8px] text-[#9CA3AF] pl-2 italic">
                        {isExcluded ? t('ganttView.excluded2') : t('ganttView.noDateAssigned')}
                      </span>
                      {hasHiddenLinks && (
                        <span className="absolute right-1 text-[8px] text-[#9CA3AF] bg-gray-50 px-1 rounded">
                          {t('ganttView.hiddenLink')}
                        </span>
                      )}
                    </div>
                  );
                }

                const isDragging = dragState?.itemId === item.id;
                let barStart = item.start_date;
                let barEnd = item.end_date || item.start_date;
                if (isDragging && dragState) {
                  barStart = dragState.previewStart;
                  barEnd = dragState.previewEnd;
                }

                const sOff = dayOffset(barStart!, rangeStart);
                const eOff = barEnd ? dayOffset(barEnd, rangeStart) : sOff;
                const barLeft = Math.max(0, sOff * DAY_W);
                const barWidth = Math.max(DAY_W, (eOff - sOff + 1) * DAY_W);

                let barBorder = barColor;
                let barBorderWidth = 1;

                let barBoxShadow: string | undefined;

                if (isActive) {
                  barBoxShadow = `0 0 0 1.5px ${barColor}, 0 0 0 3px rgba(0,0,0,0.08)`;
                } else if (isCritical && !isComplete) {
                  barBoxShadow = `0 0 0 2px #EF4444`;
                } else if (isDelayed && !isComplete) {
                  barBoxShadow = `0 0 0 1.5px #EF4444`;
                } else if (isAtRisk && !isComplete) {
                  barBoxShadow = `0 0 0 1.5px #F59E0B`;
                }

                return (
                    <div
                      key={item.id}
                      className={`relative border-b border-gray-100 flex items-center ${isHovered ? 'bg-[#FEF0E7]/30' : ''}`}
                      style={{ height: ROW_HEIGHT, boxSizing: 'border-box' }}
                      onMouseEnter={() => setHoveredRow(item.id)}
                      onMouseLeave={() => setHoveredRow(null)}
                    >
                    <div
                      data-gantt-bar
                      className={`absolute top-1/2 -translate-y-1/2 rounded-sm overflow-hidden group z-10 ${enableDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}`}
                      style={{
                        left: barLeft + 1,
                        width: Math.max(4, barWidth - 2),
                        height: BAR_HEIGHT,
                        minHeight: BAR_HEIGHT,
                        backgroundColor: barColor,
                        border: `${barBorderWidth}px solid ${barBorder}`,
                        boxShadow: barBoxShadow,
                        opacity: isDragging ? 0.85 : 1,
                      }}
                      title={`${displayTitle}: ${formatDateLabel(barStart!)} → ${formatDateLabel(barEnd || barStart!)} (${isDragging && dragState ? dragState.previewDuration : item.duration_days}d)`}
                      onPointerDown={(e) => handleBarMouseDown(e, item)}
                      onClick={(e) => handleBarClick(e, item)}
                    >
                      <div
                        className="absolute top-0 left-0 bottom-0"
                        style={{
                          width: `${ganttPct * 100}%`,
                          backgroundColor: 'rgba(255,255,255,0.3)',
                        }}
                      />
                      {isComplete && (
                        <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                          <span className="text-[8px] font-bold text-white bg-black/30 px-1 rounded">{t('ganttView.complete')}</span>
                        </div>
                      )}
                      {isCritical && !isComplete && (
                        <div
                          className="absolute inset-0 opacity-10"
                          style={{
                            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(239,68,68,0.3) 3px, rgba(239,68,68,0.3) 6px)',
                          }}
                        />
                      )}
                      {isDelayed && !isComplete && (
                        <div className="absolute inset-0 opacity-[0.06] bg-[#EF4444]" />
                      )}
                      {isAtRisk && !isComplete && (
                        <div className="absolute inset-0 opacity-[0.06] bg-[#F59E0B]" />
                      )}
                      {barWidth > BAR_LABEL_MIN_WIDTH && (
                        <span
                          className="absolute inset-0 flex items-center px-2 z-10 pointer-events-none"
                        >
                          <span
                            className="text-[13px] font-semibold truncate flex-1 min-w-0 leading-tight"
                            style={{ color: barLabelColor }}
                            title={displayTitle}
                          >
                            {displayTitle}
                          </span>
                        </span>
                      )}
                    </div>

                    {barWidth <= BAR_LABEL_MIN_WIDTH && barWidth > 0 && (
                      <span
                        className="absolute top-1/2 -translate-y-1/2 text-[12px] font-semibold px-1 py-0 rounded-sm truncate max-w-[180px] z-10 pointer-events-none"
                        style={{ left: barLeft + barWidth + 4, color: '#374151' }}
                        title={displayTitle}
                      >
                        {displayTitle}
                      </span>
                    )}

                    {hasHiddenLinks && (
                      <span className="absolute right-1 text-[8px] text-[#9CA3AF] bg-gray-50 px-1 rounded z-10">
                        {t('ganttView.hiddenLink')}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky horizontal scrollbar — always visible at bottom, synced with chart body */}
      <div
        ref={stickyScrollRef}
        className="flex-shrink-0 overflow-x-auto overflow-y-hidden border-t border-gray-200 bg-[#F9FAFB] sticky bottom-0 z-20"
        style={{ height: 14, minHeight: 14 }}
      >
        <div style={{ width: timelineWidth, height: 1 }} />
      </div>
    </div>
  );
}

const ScheduleGanttView = forwardRef(ScheduleGanttViewInner);
export default ScheduleGanttView;